import React from 'react';
import { Activity, Flame, Zap, Scale, Chart, Coffee, BarChart2 } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета базального метаболизма
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Определение цветов и текста для ИМТ
  const getBmiColor = (bmi) => {
    if (bmi < 18.5) return 'amber';
    if (bmi >= 18.5 && bmi < 25) return 'green';
    if (bmi >= 25 && bmi < 30) return 'amber';
    return 'rose';
  };

  return (
    <ToolResult title="Результаты расчета метаболизма">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Базальный метаболизм (BMR) */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Flame className="w-4 h-4"/>
            Базальный метаболизм (BMR)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatNumber(results.bmr, 0)} ккал
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Минимальный расход энергии в состоянии покоя
          </p>
        </div>

        {/* Общий расход энергии (TDEE) */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Activity className="w-4 h-4"/>
            Общий расход энергии (TDEE)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.tdee, 0)} ккал
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            С учетом уровня активности ×{results.activityFactor}
          </p>
        </div>
      </div>

      {/* Целевые показатели */}
      <div className="mb-6 bg-gradient-to-br from-purple-50 to-fuchsia-50 dark:from-purple-900/30 dark:to-fuchsia-900/30 p-5 rounded-lg border border-purple-100 dark:border-purple-800/50 shadow-sm">
        <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-3 flex items-center gap-1.5">
          <Target className="w-4 h-4"/>
          Ваша цель: {formData.goal === 'lose' ? 'Снижение веса' : formData.goal === 'gain' ? 'Набор массы' : 'Поддержание веса'}
        </h3>
        
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-purple-600 dark:text-purple-400">Целевое потребление калорий:</span>
          <span className="text-xl font-bold text-purple-800 dark:text-purple-200">
            {formatNumber(results.targetCalories, 0)} ккал в день
          </span>
        </div>
        
        {formData.goal !== 'maintain' && (
          <>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-purple-600 dark:text-purple-400">
                {formData.goal === 'lose' ? 'Дефицит калорий:' : 'Профицит калорий:'}
              </span>
              <span className="font-semibold text-purple-800 dark:text-purple-200">
                {formatNumber(Math.abs(results.targetCalories - results.tdee), 0)} ккал в день
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-purple-600 dark:text-purple-400">
                Прогнозируемое изменение веса:
              </span>
              <span className="font-semibold text-purple-800 dark:text-purple-200">
                {formatNumber(formData.weightGoal, 1)} кг в неделю
              </span>
            </div>
          </>
        )}
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Индекс массы тела (ИМТ)" 
          value={`${results.bmi} кг/м² – ${results.bmiCategory.category}`} 
          color={getBmiColor(results.bmi)} 
          icon={<Scale className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Рекомендуемое потребление воды" 
          value={`${results.waterRecommendation} л в день`} 
          color="blue" 
          icon={<Droplet className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Рекомендуемое потребление белка" 
          value={`${results.proteinRecommendation} г в день`} 
          color="amber" 
          icon={<Sandwich className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Количество приемов пищи" 
          value={`${formData.mealCount} ${getPluralMeals(formData.mealCount)} ${formData.includeSnacks ? '+ перекусы' : ''}`} 
          color="teal" 
          icon={<Coffee className="w-4 h-4"/>}
        />
        
        {results.bmiCategory.recommendation && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Info className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендация по весу</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {results.bmiCategory.recommendation}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* График визуализации распределения энергии */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start mb-4">
          <BarChart2 className="w-5 h-5 text-emerald-500 mt-0.5 mr-2" />
          <h4 className="text-base font-medium text-gray-900 dark:text-white">
            Распределение энергии
          </h4>
        </div>
        
        <div className="space-y-4">
          {/* BMR - базовый уровень */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 dark:text-gray-400">Базальный метаболизм (BMR)</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(results.bmr, 0)} ккал</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div 
                className="bg-emerald-500 h-2.5 rounded-full" 
                style={{ width: `${(results.bmr / results.tdee) * 100}%` }}
              ></div>
            </div>
          </div>
          
          {/* Активность - дополнительный расход */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 dark:text-gray-400">Расход на активность</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(results.tdee - results.bmr, 0)} ккал</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div 
                className="bg-blue-500 h-2.5 rounded-full" 
                style={{ width: `${((results.tdee - results.bmr) / results.tdee) * 100}%` }}
              ></div>
            </div>
          </div>
          
          {/* Целевое потребление - с учетом дефицита/профицита */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 dark:text-gray-400">Целевое потребление</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatNumber(results.targetCalories, 0)} ккал</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div 
                className={`${formData.goal === 'lose' ? 'bg-amber-500' : formData.goal === 'gain' ? 'bg-purple-500' : 'bg-green-500'} h-2.5 rounded-full`}
                style={{ width: `${(results.targetCalories / Math.max(results.tdee, results.targetCalories)) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "прием пищи"
const getPluralMeals = (count) => {
  count = Math.abs(parseInt(count, 10));
  if (count % 10 === 1 && count % 100 !== 11) {
    return 'прием пищи';
  } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
    return 'приема пищи';
  } else {
    return 'приемов пищи';
  }
};

// Импорт недостающих иконок
const Target = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const Droplet = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
  </svg>
);

const Sandwich = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 11v3a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-3"></path>
    <path d="M12 19H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-4"></path>
    <path d="M3 11v-3a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3"></path>
    <path d="M12 6V4"></path>
    <path d="M16 6V4"></path>
    <path d="M8 6V4"></path>
    <path d="M12 19v-3"></path>
  </svg>
);

const Info = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

export default ResultsOverview;